/**
 * Records one message in a contact's log.
 *
 * "Save to log" stores what was typed with the chosen direction and channel.
 * "Send on WhatsApp" is offered only for an outbound WhatsApp message to a
 * contact with a phone: it opens wa.me with the text prefilled in a new tab
 * and stores the message as outbound/whatsapp. Finishd never sends anything
 * itself; the realtor presses send in WhatsApp.
 *
 * "When" defaults to now: nothing is sent and the server stamps the time.
 * "Earlier…" reveals a date-time picker for logging after the fact; a picked
 * time is sent as `occurredAt`, and a time that hasn't happened yet blocks
 * saving. Send on WhatsApp is hidden while a time is picked, because that
 * message is being sent now.
 *
 * Enter inserts a newline; Ctrl+Enter (or Cmd+Enter) saves to log.
 *
 * After either save, "When should you follow up next?" is offered.
 */

import { Clock, Loader2, MessageCircle, Save } from "lucide-react";
import { type KeyboardEvent, useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLogContactMessage } from "@/features/realtor/hooks/use-realtor-crm";
import {
	type Contact,
	type MessageChannel,
	type MessageDirection,
	type MessageInput,
	whatsappUrl,
} from "@/features/realtor/services/realtor-crm-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { StudioButton } from "@/features/studio/theme";
import {
	NextStepPrompt,
	type NextStepTarget,
} from "../follow-ups/next-step-prompt";
import { parseLocalInput, toLocalInputValue } from "./message-format";

const CHANNELS: readonly MessageChannel[] = [
	"whatsapp",
	"call",
	"sms",
	"email",
	"in_person",
	"other",
];
const DIRECTIONS: readonly MessageDirection[] = ["outbound", "inbound"];
const fieldLabelClass =
	"mb-1 block text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted";
/** The server rejects a longer body. */
const MAX_BODY = 5000;

export function Composer({ contact }: { contact: Contact }) {
	const { t } = useTranslation();
	const ids = useId();
	const logMessage = useLogContactMessage();
	const [direction, setDirection] = useState<MessageDirection>("outbound");
	const [channel, setChannel] = useState<MessageChannel>("whatsapp");
	const [body, setBody] = useState("");
	// null: "now" (picker hidden). A string: the picker's value, "" if unset.
	const [when, setWhen] = useState<string | null>(null);
	const [nextStep, setNextStep] = useState<NextStepTarget | null>(null);
	const closeNextStep = useCallback(() => setNextStep(null), []);

	const text = body.trim();
	const busy = logMessage.isPending;
	const whenDate = when ? parseLocalInput(when) : null;
	const whenInFuture = whenDate !== null && whenDate.getTime() > Date.now();
	const waUrl = whatsappUrl(contact.phoneCountryCode, contact.phone, text);
	const whatsappApplies =
		direction === "outbound" && channel === "whatsapp" && waUrl !== null;
	const canSendOnWhatsapp = whatsappApplies && whenDate === null;
	const canSave = Boolean(text) && !busy && !whenInFuture;

	const save = async (
		input: Omit<MessageInput, "body">,
		failureMessage: (error: unknown) => string,
	) => {
		try {
			await logMessage.mutateAsync({
				contactId: contact.id,
				input: { ...input, body: text },
			});
			setBody("");
			setWhen(null);
			toast.success(t("realtor.messages.composer.saved"));
			setNextStep({
				contactId: contact.id,
				contactName: contact.name,
				fromLeadId: null,
			});
		} catch (error: unknown) {
			toast.error(failureMessage(error));
		}
	};

	const saveToLog = () => {
		if (!canSave) return;
		const occurredAt = whenDate ? { occurredAt: whenDate.toISOString() } : {};
		void save({ direction, channel, ...occurredAt }, (error) =>
			errorMessage(error, t("realtor.messages.composer.saveFailed")),
		);
	};

	const sendOnWhatsapp = () => {
		if (!text || busy || !canSendOnWhatsapp || !waUrl) return;
		// Opened without "noopener" so a blocked pop-up is detectable (null);
		// the opener link is cut by hand instead.
		const opened = window.open(waUrl, "_blank");
		if (!opened) {
			toast.error(t("realtor.messages.composer.popupBlocked"));
			return;
		}
		opened.opener = null;
		// WhatsApp is already open, so say that rather than the server's reason.
		void save({ direction: "outbound", channel: "whatsapp" }, () =>
			t("realtor.messages.composer.whatsappSaveFailed"),
		);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
		if (event.ctrlKey || event.metaKey) {
			event.preventDefault();
			saveToLog();
		}
	};

	return (
		<>
			<form
				className="space-y-3 border-t border-dashboard-border p-3 sm:p-4"
				onSubmit={(event) => {
					event.preventDefault();
					saveToLog();
				}}
			>
				<div className="flex flex-wrap items-end gap-3">
					<fieldset className="min-w-0">
						<legend className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted">
							{t("realtor.messages.composer.directionLabel")}
						</legend>
						<div className="inline-flex rounded-lg border border-dashboard-border bg-dashboard-bg p-0.5">
							{DIRECTIONS.map((value) => (
								<button
									key={value}
									type="button"
									aria-pressed={direction === value}
									onClick={() => setDirection(value)}
									className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
										direction === value
											? "bg-dashboard-accent text-dashboard-on-fill"
											: "text-dashboard-muted hover:text-dashboard-text"
									}`}
								>
									{t(`realtor.messages.composer.${value}`)}
								</button>
							))}
						</div>
					</fieldset>
					<div className="min-w-0">
						<label
							htmlFor={`${ids}-channel`}
							className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted"
						>
							{t("realtor.messages.composer.channelLabel")}
						</label>
						<select
							id={`${ids}-channel`}
							value={channel}
							onChange={(event) =>
								setChannel(event.target.value as MessageChannel)
							}
							className="h-9 rounded-lg border border-dashboard-border bg-dashboard-bg px-2 text-sm text-dashboard-text"
						>
							{CHANNELS.map((value) => (
								<option key={value} value={value}>
									{t(`realtor.messages.channels.${value}`)}
								</option>
							))}
						</select>
					</div>
					<div className="min-w-0">
						{when === null ? (
							<span className={fieldLabelClass}>
								{t("realtor.messages.composer.whenLabel")}
							</span>
						) : (
							<label htmlFor={`${ids}-when`} className={fieldLabelClass}>
								{t("realtor.messages.composer.whenLabel")}
							</label>
						)}
						{when === null ? (
							<div className="flex h-9 items-center gap-2 text-sm text-dashboard-text">
								<span>{t("realtor.messages.composer.whenNow")}</span>
								<button
									type="button"
									onClick={() => setWhen("")}
									className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-dashboard-accent-dark hover:underline"
								>
									<Clock className="h-4 w-4" />
									{t("realtor.messages.composer.earlier")}
								</button>
							</div>
						) : (
							<div className="flex flex-wrap items-center gap-2">
								<input
									id={`${ids}-when`}
									type="datetime-local"
									value={when}
									max={toLocalInputValue(new Date())}
									onChange={(event) => setWhen(event.target.value)}
									aria-invalid={whenInFuture}
									aria-describedby={`${ids}-when-hint`}
									className="h-9 rounded-lg border border-dashboard-border bg-dashboard-bg px-2 text-sm text-dashboard-text"
								/>
								<button
									type="button"
									onClick={() => setWhen(null)}
									className="rounded-md px-1.5 py-1 text-xs font-medium text-dashboard-accent-dark hover:underline"
								>
									{t("realtor.messages.composer.useNow")}
								</button>
							</div>
						)}
					</div>
				</div>
				{when !== null && (
					<p
						id={`${ids}-when-hint`}
						role={whenInFuture ? "alert" : undefined}
						className={`text-[11px] ${
							whenInFuture ? "text-dashboard-pill-risk" : "text-dashboard-muted"
						}`}
					>
						{whenInFuture
							? t("realtor.messages.composer.whenFuture")
							: t("realtor.messages.composer.whenHint")}
					</p>
				)}

				<div>
					<label htmlFor={`${ids}-body`} className="sr-only">
						{t("realtor.messages.composer.bodyLabel")}
					</label>
					<textarea
						id={`${ids}-body`}
						value={body}
						onChange={(event) => setBody(event.target.value)}
						onKeyDown={onKeyDown}
						maxLength={MAX_BODY}
						rows={3}
						placeholder={t(
							direction === "outbound"
								? "realtor.messages.composer.bodyPlaceholderOutbound"
								: "realtor.messages.composer.bodyPlaceholderInbound",
						)}
						aria-describedby={`${ids}-shortcut`}
						className="w-full resize-y rounded-lg border border-dashboard-border bg-dashboard-bg px-3 py-2 text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40"
					/>
					<p
						id={`${ids}-shortcut`}
						className="mt-1 text-[11px] text-dashboard-muted"
					>
						{t("realtor.messages.composer.shortcutHint")}
					</p>
				</div>

				<div className="flex flex-wrap items-center justify-end gap-2">
					{whatsappApplies && !canSendOnWhatsapp && (
						<p className="min-w-0 flex-1 text-[11px] text-dashboard-muted">
							{t("realtor.messages.composer.whatsappNeedsNow")}
						</p>
					)}
					{canSendOnWhatsapp && (
						<div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-end">
							<p
								id={`${ids}-wa-hint`}
								className="text-[11px] text-dashboard-muted sm:text-right"
							>
								{t("realtor.messages.composer.sendWhatsappHint")}
							</p>
							<StudioButton
								variant="secondary"
								size="sm"
								className="shrink-0"
								icon={MessageCircle}
								disabled={!text || busy}
								aria-describedby={`${ids}-wa-hint`}
								onClick={sendOnWhatsapp}
							>
								{t("realtor.messages.composer.sendWhatsapp")}
							</StudioButton>
						</div>
					)}
					<StudioButton
						type="submit"
						size="sm"
						className="shrink-0"
						icon={busy ? undefined : Save}
						disabled={!canSave}
					>
						{busy && <Loader2 className="h-4 w-4 animate-spin" />}
						{busy
							? t("realtor.messages.composer.saving")
							: t("realtor.messages.composer.saveToLog")}
					</StudioButton>
				</div>
			</form>
			<NextStepPrompt target={nextStep} onClose={closeNextStep} />
		</>
	);
}
